import {EmptyPolicyNumber, PolicyNumber, ValidPolicyNumber} from 'ApplicationEntry/models/EnteredPolicyNumber';
import {PackageModel, UnassignedPackageModel} from 'ApplicationEntry/models/ProductsRiders/PackageModel';
import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToPartialMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

type CriticalIllnessNYProps = {
  planType: StringUnion;
  benefitAmount: number;
  premium: StringUnion;
  calculatedPremium: StringUnion;
  cwa: StringUnion;
  insuredSC: StringUnion;
  spouseSC: StringUnion;
  section125: boolean;
  replacement: boolean;
  policyNumber: PolicyNumber;
  situsState: StringUnion;
  wellnessUnits: StringUnion;
  ciOption: boolean;
  secondEventCI: boolean;
  jStamp: boolean;
  packageSource: StringUnion;
  packageSourceList: PackageModel;
};

export class InvalidNewYorkCriticalIllnessNY {
  public readonly key = 'InvalidNewYorkCriticalIllnessNY';

  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export class ValidNewYorkCriticalIllnessNY {
  public static createApp({
    planType,
    benefitAmount,
    premium,
    calculatedPremium,
    cwa,
    insuredSC,
    spouseSC,
    section125,
    replacement,
    policyNumber,
    situsState,
    wellnessUnits,
    ciOption,
    secondEventCI,
    jStamp,
    packageSource,
    packageSourceList,
  }: CriticalIllnessNYProps) {
    return this.create(
      planType,
      benefitAmount,
      premium,
      calculatedPremium,
      cwa,
      insuredSC,
      spouseSC,
      section125,
      replacement,
      policyNumber,
      situsState,
      wellnessUnits,
      ciOption,
      secondEventCI,
      jStamp,
      packageSource,
      packageSourceList
    );
  }

  public static create(
    planType: StringUnion,
    benefitAmount: number,
    premium: StringUnion,
    calculatedPremium: StringUnion,
    cwa: StringUnion,
    insuredSC: StringUnion,
    spouseSC: StringUnion,
    section125: boolean,
    replacement: boolean,
    policyNumber: PolicyNumber,
    situsState: StringUnion,
    wellnessUnits: StringUnion,
    ciOption: boolean,
    secondEventCI: boolean,
    jStamp: boolean,
    packageSource: StringUnion,
    packageSourceList: PackageModel
  ) {
    return planType &&
      benefitAmount &&
      isNonEmptyString(premium) &&
      isNonEmptyString(cwa) &&
      insuredSC &&
      section125 &&
      replacement &&
      policyNumber
      ? new ValidNewYorkCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        )
      : new InvalidNewYorkCriticalIllnessNY(
          planType,
          benefitAmount,
          premium,
          calculatedPremium,
          cwa,
          insuredSC,
          spouseSC,
          section125,
          replacement,
          policyNumber,
          situsState,
          wellnessUnits,
          ciOption,
          secondEventCI,
          jStamp,
          packageSource,
          packageSourceList
        );
  }

  public readonly key = 'ValidNewYorkCriticalIllnessNY';
  constructor(
    public readonly planType: StringUnion,
    public readonly benefitAmount: number,
    public readonly premium: StringUnion,
    public readonly calculatedPremium: StringUnion,
    public readonly cwa: StringUnion,
    public readonly insuredSC: StringUnion,
    public readonly spouseSC: StringUnion,
    public readonly section125: boolean,
    public readonly replacement: boolean,
    public readonly policyNumber: PolicyNumber,
    public readonly situsState: StringUnion,
    public readonly wellnessUnits: StringUnion,
    public readonly ciOption: boolean,
    public readonly secondEventCI: boolean,
    public readonly jStamp: boolean,
    public readonly packageSource: StringUnion,
    public readonly packageSourceList: PackageModel
  ) {}
}

export type NewYorkCriticalIllnessNY = InvalidNewYorkCriticalIllnessNY | ValidNewYorkCriticalIllnessNY;

export type NewYorkCriticalIllnessNYOptions<TReturn> = ToPartialMatchOptions<NewYorkCriticalIllnessNY, 'key', TReturn>;

export const matchGroupCriticalIllnessNY = <TReturn>(options: NewYorkCriticalIllnessNYOptions<TReturn>) => (
  app: NewYorkCriticalIllnessNY
): TReturn => {
  switch (app.key) {
    case 'InvalidNewYorkCriticalIllnessNY':
      return options.InvalidNewYorkCriticalIllnessNY
        ? options.InvalidNewYorkCriticalIllnessNY(app)
        : options.default(app);
    case 'ValidNewYorkCriticalIllnessNY':
      return options.ValidNewYorkCriticalIllnessNY ? options.ValidNewYorkCriticalIllnessNY(app) : options.default(app);
  }
};

export const EmptyNewYorkCriticalIllnessNY = new InvalidNewYorkCriticalIllnessNY(
  new EmptyString(),
  0,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  EmptyPolicyNumber,
  new EmptyString(),
  new EmptyString(),
  false,
  false,
  false,
  new EmptyString(),
  new UnassignedPackageModel()
);

export const changePlanType = (planType: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, planType: NonEmptyString.create(planType)});

export const changeBenefitAmount = (benefitAmount: number) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, benefitAmount});

export const changePremium = (premium: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, premium: NonEmptyString.create(premium)});

  export const changeCalculatedPremium = (calculatedPremium: string) => (app: NewYorkCriticalIllnessNY) =>
    ValidNewYorkCriticalIllnessNY.createApp({...app, calculatedPremium: NonEmptyString.create(calculatedPremium)});

export const changeCWA = (cwa: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, cwa: NonEmptyString.create(cwa)});

export const changeInsuredSC = (insuredSC: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, insuredSC: NonEmptyString.create(insuredSC)});

export const changeSpouseSC = (spouseSC: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, spouseSC: NonEmptyString.create(spouseSC)});

export const changeSection125 = (section125: boolean) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, section125});

export const changeReplacement = (replacement: boolean) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({
    ...app,
    replacement,
    policyNumber: ValidPolicyNumber.create(new EmptyString()),
  });

export const changePolicyNumber = (policyNumber: StringUnion) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, policyNumber: ValidPolicyNumber.create(policyNumber)});

export const changeSitusState = (situsState: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, situsState: NonEmptyString.create(situsState)});

export const changeWellnessUnit = (wellnessUnits: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, wellnessUnits: NonEmptyString.create(wellnessUnits)});

export const changeCIOption = (ciOption: boolean) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, ciOption});

export const changeSecondEventCI = (secondEventCI: boolean) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, secondEventCI});

export const changeJStamp = (jStamp: boolean) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, jStamp});

export const changePackageSource = (packageSource: string) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, packageSource: NonEmptyString.create(packageSource)});

export const changePackageSourceList = (packageSourceList: PackageModel) => (app: NewYorkCriticalIllnessNY) =>
  ValidNewYorkCriticalIllnessNY.createApp({...app, packageSourceList});
